/**
 * FloatingTools — renders the 3 mini-windows (Notepad / Sticky Notes /
 * Calculator) and the minimised tray. Mounted once inside AppLayout.
 *
 * Windows are draggable (mousedown on header) + resizable (mousedown on the
 * bottom-right corner). State lives in FloatingToolsContext.
 */
import React, { useEffect, useRef, useState } from "react";
import { useFloatingTools } from "@/context/FloatingToolsContext";
import {
  FileText, Note, Calculator as CalcIcon, X, Minus, CaretUp,
} from "@phosphor-icons/react";
import Notepad from "./Notepad";
import NotesPanel from "./NotesPanel";
import Calculator from "./Calculator";

const TOOLS = {
  notepad:    { label: "Notepad",      Icon: FileText, Component: Notepad },
  notes:      { label: "Sticky Notes", Icon: Note,     Component: NotesPanel },
  calculator: { label: "Calculator",   Icon: CalcIcon, Component: Calculator },
};

export default function FloatingTools() {
  const { windows, closeTool, minimizeTool, updateWindow, bringToFront } = useFloatingTools();
  const minimizedIds = Object.keys(windows).filter((k) => windows[k].minimized);

  return (
    <>
      {/* Minimised tray */}
      {minimizedIds.length > 0 && (
        <div className="fixed bottom-4 left-4 z-30 flex items-end gap-1.5" data-testid="floating-tools-tray">
          {minimizedIds.map((id) => {
            const tool = TOOLS[id]; if (!tool) return null;
            const Icon = tool.Icon;
            return (
              <button
                key={id}
                onClick={() => minimizeTool(id)}
                className="flex items-center gap-1.5 px-3 py-2 bg-[var(--text-primary)] text-white text-xs shadow-lg hover:opacity-90"
                data-testid={`floating-restore-${id}`}
              >
                <Icon size={12} />
                <span className="font-mono uppercase tracking-widest text-[10px]">{tool.label}</span>
                <CaretUp size={10} />
              </button>
            );
          })}
        </div>
      )}

      {/* Windows */}
      {Object.entries(windows).map(([id, win]) => {
        if (win.minimized) return null;
        const tool = TOOLS[id]; if (!tool) return null;
        const Component = tool.Component;
        return (
          <ToolWindow
            key={id}
            id={id}
            label={tool.label}
            Icon={tool.Icon}
            win={win}
            onClose={() => closeTool(id)}
            onMinimize={() => minimizeTool(id)}
            onMove={(patch) => updateWindow(id, patch)}
            onFocus={() => bringToFront(id)}
          >
            <Component dense />
          </ToolWindow>
        );
      })}
    </>
  );
}

function ToolWindow({ id, label, Icon, win, onClose, onMinimize, onMove, onFocus, children }) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(null);

  const onMouseDownHeader = (e) => {
    if (e.target.closest("button")) return;  // clicks on header buttons shouldn't drag
    onFocus();
    setDragging({ dx: e.clientX - win.x, dy: e.clientY - win.y });
  };
  const onMouseDownResize = (e) => {
    e.stopPropagation();
    onFocus();
    setDragging({ resize: true, startW: win.w, startH: win.h, startX: e.clientX, startY: e.clientY });
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove2 = (e) => {
      if (dragging.resize) {
        onMove({
          w: Math.max(280, dragging.startW + (e.clientX - dragging.startX)),
          h: Math.max(280, dragging.startH + (e.clientY - dragging.startY)),
        });
      } else {
        onMove({
          x: Math.max(0, e.clientX - dragging.dx),
          y: Math.max(0, e.clientY - dragging.dy),
        });
      }
    };
    const onUp = () => setDragging(null);
    document.addEventListener("mousemove", onMove2);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove2);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragging, onMove]);

  return (
    <div
      ref={ref}
      className="fixed bg-white border border-[var(--border)] shadow-2xl flex flex-col"
      style={{ left: win.x, top: win.y, width: win.w, height: win.h, zIndex: win.z || 30 }}
      onMouseDown={onFocus}
      data-testid={`floating-window-${id}`}
    >
      {/* Header (drag handle) */}
      <div
        onMouseDown={onMouseDownHeader}
        className="cursor-move px-3 py-2 border-b border-[var(--border)] bg-[var(--bg-elev)] flex items-center justify-between select-none"
        data-testid={`floating-window-header-${id}`}
      >
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-primary)]">
          <Icon size={13} weight="duotone" />
          {label}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onMinimize} className="p-1 hover:bg-black/5" title="Minimise" data-testid={`floating-window-min-${id}`}>
            <Minus size={12} />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-red-100 text-red-600" title="Close" data-testid={`floating-window-close-${id}`}>
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden">{children}</div>

      {/* Resize handle (bottom-right corner) */}
      <div
        onMouseDown={onMouseDownResize}
        className="absolute bottom-0 right-0 w-3.5 h-3.5 cursor-se-resize"
        style={{ background: "linear-gradient(135deg, transparent 50%, var(--border) 50%)" }}
        data-testid={`floating-window-resize-${id}`}
      />
    </div>
  );
}
